import { useEffect, useState } from "react";
import { Loader2, Video, Calendar, CheckCircle, Copy, BellRing, CreditCard, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const TYPES = [
  { key: "teleconsultation", label: "Téléconsultation médicale", desc: "Consultation individuelle avec le médecin" },
  { key: "formation", label: "Formation en ligne", desc: "Session de groupe animée par le formateur" },
  { key: "expertise", label: "Réunion d'expertise", desc: "Conseil professionnel en petit groupe" },
  { key: "webinaire", label: "Webinaire public", desc: "Conférence et présentation académique" },
];

export default function PrendreRDV() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", dob: "", phone: "", whatsapp: "", email: "", motif: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [remChoices, setRemChoices] = useState([]);
  const [reminders, setReminders] = useState({ offsets: [], email: true, whatsapp: true, sms: true });
  const [prices, setPrices] = useState({});
  const [paid, setPaid] = useState(false);
  const [paying, setPaying] = useState("");
  const [payOp, setPayOp] = useState("orange");

  useEffect(() => { api.get("/visio/prices").then((r) => setPrices(r.data || {})).catch(() => {}); }, []);
  const fmtXof = (n) => new Intl.NumberFormat("fr-FR").format(n) + " FCFA";

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("type");
    if (t && TYPES.some((x) => x.key === t)) { setType(t); setStep(2); }
  }, []);

  useEffect(() => {
    api.get("/visio/reminder-defaults").then((r) => {
      setRemChoices(r.data.choices || []);
      const d = r.data.default || {};
      setReminders({ offsets: d.offsets || [], email: d.email !== false, whatsapp: d.whatsapp !== false, sms: d.sms !== false });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (step === 2 && type) {
      api.get(`/visio/slots?type=${type}`).then((r) => setSlots(r.data)).catch(() => setSlots([]));
    }
  }, [step, type]);

  const toggleOffset = (min) => setReminders((r) => ({
    ...r,
    offsets: r.offsets.includes(min) ? r.offsets.filter((o) => o !== min) : [...r.offsets, min],
  }));

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/visio/appointments", { slot_id: slot.id, ...form, reminders });
      setDone(data); setStep(5);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });

  const payStripe = async () => {
    setPaying("stripe");
    try { const { data } = await api.post("/payments/rdv/stripe/checkout", { appointment_id: done.id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e)); setPaying(""); }
  };
  const payMobile = async () => {
    if (!form.phone && !form.whatsapp) { toast.error("Renseignez un numéro de téléphone"); return; }
    setPaying("mobile");
    try { await api.post("/payments/rdv/mobile-money", { appointment_id: done.id, operator: payOp, phone: form.phone || form.whatsapp }); setPaid(true); toast.success("Paiement Mobile Money confirmé"); }
    catch (e) { toast.error(apiError(e)); } finally { setPaying(""); }
  };

  const openVisioWindow = (token) => {
    const url = `${window.location.origin}/visio/${token}`;
    // Nouvel onglet plein écran dédié (option recommandée : simple et robuste)
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12" data-testid="rdv-page">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Video size={20} /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-navy">Prendre rendez-vous</h1>
          <p className="text-sm text-slate-500">Cabinet CARIS Santé — sessions sécurisées en ligne</p>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-3" data-testid="rdv-step-type">
          <Label className="font-mono text-xs uppercase tracking-widest text-ciel">Type de consultation</Label>
          {TYPES.map((t) => (
            <div key={t.key} data-testid={`rdv-type-${t.key}`}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-ciel hover:shadow-sm">
              <button onClick={() => { setType(t.key); setStep(2); }} className="flex-1 text-left">
                <p className="font-display font-semibold text-navy">{t.label}</p><p className="text-xs text-slate-500">{t.desc}</p>
              </button>
              <button data-testid={`rdv-type-link-${t.key}`} title="Copier le lien direct de programmation"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/prendre-rendez-vous?type=${t.key}`); toast.success("Lien direct copié"); }}
                className="ml-3 rounded-lg border border-slate-200 p-2 text-slate-400 hover:text-ciel"><Copy size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {step === 2 && (
        <div data-testid="rdv-step-slots">
          <Label className="font-mono text-xs uppercase tracking-widest text-ciel">Créneaux disponibles</Label>
          {slots.length === 0 ? (
            <p className="mt-4 rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">Aucun créneau disponible pour ce type. Contactez-nous via WhatsApp.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {slots.map((s) => (
                <button key={s.id} data-testid={`rdv-slot-${s.id}`} onClick={() => { setSlot(s); setStep(3); }}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-ciel">
                  <span className="text-sm text-navy">{fmt(s.start)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{s.duration_min} min</span>
                </button>
              ))}
            </div>
          )}
          <Button variant="ghost" className="mt-4" onClick={() => setStep(1)}>← Changer de type</Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="rdv-step-info">
          <Label className="font-mono text-xs uppercase tracking-widest text-ciel">Vos informations</Label>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Nom et prénoms</Label><Input data-testid="rdv-name" value={form.name} className="mt-1.5" onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Date de naissance</Label><Input type="date" value={form.dob} className="mt-1.5" onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Téléphone</Label><Input value={form.phone} className="mt-1.5" onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">WhatsApp</Label><Input value={form.whatsapp} className="mt-1.5" onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" data-testid="rdv-email" value={form.email} className="mt-1.5" onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Motif de consultation</Label><Textarea data-testid="rdv-motif" value={form.motif} className="mt-1.5" onChange={(e) => setForm({ ...form, motif: e.target.value })} /></div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="rdv-reminders">
            <div className="flex items-center gap-2">
              <BellRing size={15} className="text-ciel" />
              <p className="font-display text-sm font-semibold text-navy">Rappels automatiques</p>
            </div>
            <p className="mt-1 text-xs text-slate-500">Choisissez quand recevoir un rappel avant votre session.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {remChoices.map((c) => {
                const on = reminders.offsets.includes(c.min);
                return (
                  <button key={c.min} type="button" data-testid={`rdv-rem-${c.min}`} onClick={() => toggleOffset(c.min)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-all ${on ? "border-ciel bg-ciel text-white" : "border-slate-200 bg-white text-slate-600 hover:border-ciel"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="rdv-rem-email" checked={reminders.email} onCheckedChange={(v) => setReminders((r) => ({ ...r, email: v }))} />
                Par email
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="rdv-rem-whatsapp" checked={reminders.whatsapp} onCheckedChange={(v) => setReminders((r) => ({ ...r, whatsapp: v }))} />
                Par WhatsApp
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="rdv-rem-sms" checked={reminders.sms} onCheckedChange={(v) => setReminders((r) => ({ ...r, sms: v }))} />
                Par SMS
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(2)}>← Retour</Button>
            <Button data-testid="rdv-confirm" disabled={!form.name || !form.email} onClick={book} className="flex-1 rounded-full bg-ciel text-white hover:bg-sky-700">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirmer le rendez-vous"}
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400">{prices[type] > 0 ? `Tarif : ${fmtXof(prices[type])} · réglable en ligne après réservation ou sur place` : "Rendez-vous gratuit / sur invitation"}</p>
        </div>
      )}

      {step === 5 && done && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="rdv-done">
          <CheckCircle size={44} className="mx-auto text-emerald-500" />
          <h2 className="mt-4 font-display text-xl font-bold text-navy">Rendez-vous confirmé</h2>
          <p className="mt-2 text-sm text-slate-600">Un email de confirmation vient de vous être envoyé avec le lien de connexion.</p>
          <div className="mt-5 rounded-xl bg-white p-4">
            <p className="text-xs text-slate-400">Votre code PIN d'accès</p>
            <p className="font-display text-3xl font-bold tracking-[0.3em] text-navy">{done.pin}</p>
          </div>
          <Button data-testid="rdv-goto-session" className="mt-5 rounded-full bg-navy text-white hover:bg-navy-light" onClick={() => openVisioWindow(done.join_token)}>
            Ouvrir ma session vidéo
          </Button>
          <p className="mt-2 text-xs text-slate-400">La visioconférence s'ouvre dans un nouvel onglet plein écran dédié.</p>

          {done.price_xof > 0 && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 text-left" data-testid="rdv-payment">
              {paid || done.paid ? (
                <p className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-600" data-testid="rdv-paid"><CheckCircle size={18} /> Consultation réglée — merci !</p>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-navy">Régler ma consultation</p>
                    <span className="font-display text-lg font-bold text-navy">{fmtXof(done.price_xof)}</span>
                  </div>
                  <p className="mb-4 text-xs text-slate-500">Paiement optionnel : vous pouvez régler en ligne maintenant ou sur place.</p>
                  <Button data-testid="rdv-pay-stripe" disabled={!!paying} onClick={payStripe} className="mb-3 w-full rounded-full bg-ciel text-white hover:bg-sky-700">
                    {paying === "stripe" ? <Loader2 size={16} className="animate-spin" /> : <><CreditCard size={16} className="mr-2" /> Payer par carte bancaire</>}
                  </Button>
                  <div className="flex items-center gap-2">
                    <select value={payOp} onChange={(e) => setPayOp(e.target.value)} data-testid="rdv-pay-operator" className="h-10 rounded-lg border border-slate-200 px-2 text-sm">
                      <option value="orange">Orange Money</option>
                      <option value="mtn">MTN MoMo</option>
                      <option value="wave">Wave</option>
                      <option value="moov">Moov Money</option>
                    </select>
                    <Button data-testid="rdv-pay-mobile" disabled={!!paying} onClick={payMobile} variant="outline" className="flex-1 rounded-full">
                      {paying === "mobile" ? <Loader2 size={16} className="animate-spin" /> : <><Smartphone size={16} className="mr-2" /> Mobile Money</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
